/**
 * SMA Wishlist v1.0
 * ─────────────────────────────────────────────────────────────────────
 * Pure-JS wishlist powered by localStorage.
 * Storage key: 'sma_wishlist' = ['handle1', 'handle2', ...]
 *
 * Public API on window.SMAWishlist:
 *   .add(handle)         add product handle, fires 'sma:wishlist:change'
 *   .remove(handle)      remove product handle
 *   .toggle(handle)      add if not present, remove if present
 *   .has(handle)         returns true/false
 *   .all()               returns array of handles
 *   .count()             returns number of items
 *   .clear()             empties wishlist
 *
 * Custom event fired on document:
 *   'sma:wishlist:change' — detail: { handles: [...], count: N }
 *
 * Use this event to update header badge, heart icons everywhere, etc.
 */

(function () {
  'use strict';

  const KEY = 'sma_wishlist';
  const CHANGE_EVENT = 'sma:wishlist:change';

  /* ─── Storage helpers ─── */
  function read() {
    try {
      const raw = localStorage.getItem(KEY);
      const arr = raw ? JSON.parse(raw) : [];
      return Array.isArray(arr) ? arr.filter(h => typeof h === 'string' && h) : [];
    } catch (e) { return []; }
  }
  function write(handles) {
    try { localStorage.setItem(KEY, JSON.stringify(handles)); } catch (e) {}
    document.dispatchEvent(new CustomEvent(CHANGE_EVENT, {
      detail: { handles: handles.slice(), count: handles.length }
    }));
  }

  /* ─── Public API ─── */
  const SMAWishlist = {
    all() { return read(); },
    count() { return read().length; },
    has(handle) { return read().indexOf(handle) !== -1; },
    add(handle) {
      const cur = read();
      if (cur.indexOf(handle) === -1) { cur.unshift(handle); write(cur); }
    },
    remove(handle) {
      write(read().filter(h => h !== handle));
    },
    toggle(handle) {
      this.has(handle) ? this.remove(handle) : this.add(handle);
    },
    clear() { write([]); }
  };
  window.SMAWishlist = SMAWishlist;

  /* ─── Page renderer (only runs on /pages/wishlists or /pages/wishlist) ─── */
  function initPage() {
    const root = document.getElementById('sma-wishlist');
    if (!root) return;

    const grid = root.querySelector('#smw-grid');
    const empty = root.querySelector('#smw-empty');
    const loading = root.querySelector('#smw-loading');
    const toolbar = root.querySelector('#smw-toolbar');
    const countEl = root.querySelector('#smw-count');
    const heroCount = root.querySelector('#smw-hero-count');
    const clearBtn = root.querySelector('#smw-clear-all');
    const addAllBtn = root.querySelector('#smw-add-all');
    const toast = root.querySelector('#smw-toast');

    function showToast(msg, ms = 2500) {
      if (!toast) return;
      toast.textContent = msg;
      toast.classList.add('show');
      clearTimeout(toast._t);
      toast._t = setTimeout(() => toast.classList.remove('show'), ms);
    }

    function inrFormat(n) {
      try {
        return '₹' + Number(n / 100).toLocaleString('en-IN', { maximumFractionDigits: 0 });
      } catch (e) { return '₹' + (n / 100).toFixed(0); }
    }

    function renderCard(p) {
      const img = (p.featured_image || (p.images && p.images[0]) || '').replace(/^https?:/, '');
      const inStock = p.available !== false;
      const variant = (p.variants && p.variants[0]) || {};
      const price = variant.price || p.price;
      const variantId = variant.id || '';
      const cat = (p.product_type || '').trim();

      return `
        <div class="smw__card" data-handle="${p.handle}">
          <button class="smw__card-remove" aria-label="Remove from wishlist" data-action="remove">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
          <span class="smw__card-badge ${inStock ? '' : 'smw__card-badge--out'}">● ${inStock ? 'In stock' : 'Out of stock'}</span>
          <a href="/products/${p.handle}" class="smw__card-media">
            <img src="${img}&width=600" alt="${p.title}" loading="lazy" ${inStock ? '' : 'style="opacity:.6"'}>
          </a>
          <div class="smw__card-body">
            <a href="/products/${p.handle}">
              <div class="smw__card-title">${p.title}</div>
              ${cat ? `<div class="smw__card-cat">${cat}</div>` : ''}
            </a>
            <div class="smw__card-price">${inrFormat(price)}</div>
            ${inStock && p.variants && p.variants.length > 1 ? `
              <button type="button" class="smw__card-cta" data-sma-quick-buy="${p.handle}">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/></svg>
                Choose options
              </button>
            ` : inStock ? `
              <button class="smw__card-cta" data-action="add-to-cart" data-vid="${variantId}">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/></svg>
                Add to cart
              </button>
            ` : `
              <button class="smw__card-cta smw__card-cta--out" disabled>Out of stock</button>
            `}
          </div>
        </div>
      `;
    }

    async function fetchProduct(handle) {
      try {
        const r = await fetch(`/products/${handle}.js`, { headers: { 'Accept': 'application/json' } });
        if (!r.ok) return null;
        return await r.json();
      } catch (e) { return null; }
    }

    async function render() {
      const handles = SMAWishlist.all();
      if (loading) loading.style.display = handles.length === 0 ? 'none' : 'block';

      if (handles.length === 0) {
        if (empty) empty.style.display = 'block';
        if (grid) grid.style.display = 'none';
        if (toolbar) toolbar.style.display = 'none';
        if (heroCount) heroCount.textContent = '0 products saved';
        if (countEl) countEl.textContent = '0';
        return;
      }

      // Fetch all products in parallel
      const products = await Promise.all(handles.map(fetchProduct));
      const validProducts = products.filter(p => p && p.handle);

      // Remove dead handles from storage
      const validHandles = validProducts.map(p => p.handle);
      const removed = handles.filter(h => !validHandles.includes(h));
      if (removed.length > 0) {
        write(validHandles); // silently prune
      }

      if (loading) loading.style.display = 'none';
      if (empty) empty.style.display = validProducts.length === 0 ? 'block' : 'none';
      if (toolbar) toolbar.style.display = validProducts.length === 0 ? 'none' : 'flex';
      if (grid) {
        grid.style.display = validProducts.length === 0 ? 'none' : 'grid';
        grid.innerHTML = validProducts.map(renderCard).join('');
      }
      if (countEl) countEl.textContent = validProducts.length;
      if (heroCount) heroCount.textContent = validProducts.length === 1 ? '1 product saved' : `${validProducts.length} products saved`;

      // Attach card actions
      grid.querySelectorAll('[data-action="remove"]').forEach(btn => {
        btn.addEventListener('click', e => {
          e.preventDefault(); e.stopPropagation();
          const card = btn.closest('.smw__card');
          const handle = card.dataset.handle;
          card.style.transition = 'all .25s';
          card.style.opacity = '0';
          card.style.transform = 'scale(0.9)';
          setTimeout(() => {
            SMAWishlist.remove(handle);
            render();
            showToast('Removed from wishlist');
          }, 250);
        });
      });
      grid.querySelectorAll('[data-action="add-to-cart"]').forEach(btn => {
        btn.addEventListener('click', async e => {
          e.preventDefault(); e.stopPropagation();
          const vid = btn.dataset.vid;
          if (!vid) return;
          btn.disabled = true;
          btn.textContent = 'Adding…';
          try {
            // Shared helper (assets/sma-cart.js): re-renders + opens the drawer, syncs badges
            await window.SMACart.add({ items: [{ id: parseInt(vid, 10), quantity: 1 }] }, { source: btn });
            showToast('✓ Added to cart');
            btn.textContent = 'Added ✓';
            setTimeout(() => { btn.disabled = false; btn.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/></svg> Add to cart'; }, 1500);
          } catch (e) {
            showToast(e && e.message ? e.message : 'Could not add — try again');
            btn.disabled = false;
            btn.textContent = 'Add to cart';
          }
        });
      });
    }

    if (clearBtn) clearBtn.addEventListener('click', () => {
      if (confirm('Clear your entire wishlist?')) {
        SMAWishlist.clear();
        render();
        showToast('Wishlist cleared');
      }
    });

    if (addAllBtn) addAllBtn.addEventListener('click', async () => {
      const handles = SMAWishlist.all();
      if (handles.length === 0) return;
      addAllBtn.disabled = true;
      addAllBtn.textContent = 'Adding…';
      const products = await Promise.all(handles.map(fetchProduct));
      const items = products
        .filter(p => p && p.available !== false && p.variants && p.variants[0])
        .map(p => ({ id: p.variants[0].id, quantity: 1 }));
      if (items.length === 0) {
        showToast('No in-stock items to add');
        addAllBtn.disabled = false;
        addAllBtn.textContent = 'Add all to cart';
        return;
      }
      try {
        const r = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items })
        });
        if (r.ok) {
          showToast(`✓ ${items.length} items added to cart`);
          // Optionally redirect to cart
          setTimeout(() => { window.location.href = '/cart'; }, 1200);
        } else {
          showToast('Could not add all — please try one by one');
          addAllBtn.disabled = false;
          addAllBtn.textContent = 'Add all to cart';
        }
      } catch (e) {
        showToast('Network error');
        addAllBtn.disabled = false;
        addAllBtn.textContent = 'Add all to cart';
      }
    });

    // Re-render on storage changes (e.g., wishlist toggled in another tab/page)
    document.addEventListener(CHANGE_EVENT, render);
    window.addEventListener('storage', e => { if (e.key === KEY) render(); });

    render();
  }

  /* ─── Header heart icon badge updater (runs on every page) ─── */
  function updateHeaderBadge() {
    const count = SMAWishlist.count();
    document.querySelectorAll('[data-wishlist-count]').forEach(el => {
      el.textContent = count > 0 ? count : '';
      el.style.display = count > 0 ? '' : 'none';
    });
  }

  /* ─── PDP / collection card heart-icon binding (runs on every page) ─── */
  function bindHeartIcons() {
    document.querySelectorAll('[data-wishlist-toggle]').forEach(btn => {
      if (btn._smw_bound) return;
      btn._smw_bound = true;
      const handle = btn.dataset.wishlistToggle;
      if (!handle) return;
      // Set initial state
      if (SMAWishlist.has(handle)) btn.classList.add('is-wishlisted');
      btn.addEventListener('click', e => {
        e.preventDefault(); e.stopPropagation();
        SMAWishlist.toggle(handle);
        btn.classList.toggle('is-wishlisted', SMAWishlist.has(handle));
      });
    });
  }

  function init() {
    initPage();
    updateHeaderBadge();
    bindHeartIcons();
    document.addEventListener(CHANGE_EVENT, () => {
      updateHeaderBadge();
      // Re-bind heart icons (in case DOM changed)
      bindHeartIcons();
    });
    // Re-bind on DOM mutations (e.g., infinite scroll or AJAX section render)
    if (typeof MutationObserver === 'function') {
      const obs = new MutationObserver(() => bindHeartIcons());
      obs.observe(document.body, { childList: true, subtree: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
